// The pages to learn a motif from: `references.json` beside this file, one
// entry per page and motif (a family, or a family and a variation), each
// verified by hand on 2026-10-12 (the title and the URL fetched, the
// summary written from the page). `referencesFor` picks the ones for one
// motif: the variation's own entries and the family's behind them, so a
// variation with no page of its own (a skewer of a rook, a hanging pawn)
// still gets its family's; the same page listed under both is kept once;
// lichess first (its lessons before its studies), then Wikipedia, then
// chess.com, since a drill teaches more than an article; at most three.
// Adding a page is adding an entry to the JSON; the test checks every
// entry's motif against the scheme and every URL is https and unique.
import { isMotifFamily, isMotifVariation, type Motif, type MotifFamily, type Reference, type ReferenceKind, type ReferenceSource } from '@chess/api-mistakes';
import data from './references.json' with { type: 'json' };

/** One entry of `references.json`: a reference and the motif it teaches. */
export interface ReferenceEntry extends Reference {
  family: MotifFamily;
  /** The variation it is specific to, or null for a page on the family as a whole. */
  variation: string | null;
}

export const REFERENCES: readonly ReferenceEntry[] = data as ReferenceEntry[];

/** How many pages the card shows. */
export const MAX_REFERENCES = 3;

/** The order the sources come in: a lichess lesson first, then its studies, then Wikipedia, then chess.com. */
export function sourceRank(ref: Pick<Reference, 'source' | 'kind'>): number {
  const source: Record<ReferenceSource, number> = { lichess: 0, wikipedia: 2, chesscom: 3 };
  return ref.source === 'lichess' ? (ref.kind === 'lesson' ? 0 : 1) : source[ref.source];
}

const byRank = (a: Reference, b: Reference) => sourceRank(a) - sourceRank(b);

/** Whether the entry's motif is one the scheme has. */
export function isValidEntry(entry: { family: string; variation: string | null; kind: string; source: string }): boolean {
  if (!isMotifFamily(entry.family)) return false;
  if (entry.variation !== null && !isMotifVariation(entry.family, entry.variation)) return false;
  return (['lesson', 'study', 'article'] as ReferenceKind[]).includes(entry.kind as ReferenceKind) && (['lichess', 'wikipedia', 'chesscom'] as ReferenceSource[]).includes(entry.source as ReferenceSource);
}

/**
 * The references for a motif, from `entries` (the JSON by default): the
 * variation's own entries, then the family's, the same URL kept once (the
 * variation's copy wins), in source order (`sourceRank`, a stable sort, so
 * the variation's entry stays ahead of the family's of the same source).
 * One page per source comes first, so the three slots read lichess,
 * Wikipedia, chess.com when the motif has all three, rather than two
 * Wikipedia pages on the same idea; the slots left are filled by the rest in
 * the same order. At most `MAX_REFERENCES`.
 */
export function referencesFor(motif: Motif, entries: readonly ReferenceEntry[] = REFERENCES): Reference[] {
  const own = motif.variation ? entries.filter((e) => e.family === motif.family && e.variation === motif.variation) : [];
  const family = entries.filter((e) => e.family === motif.family && e.variation === null);
  const seen = new Set<string>();
  const candidates: Reference[] = [];
  for (const { family: _f, variation: _v, ...ref } of [...own, ...family]) {
    if (seen.has(ref.url)) continue;
    seen.add(ref.url);
    candidates.push(ref);
  }
  candidates.sort(byRank);
  const sources = new Set<ReferenceSource>();
  const firstPerSource = candidates.filter((r) => !sources.has(r.source) && sources.add(r.source));
  const rest = candidates.filter((r) => !firstPerSource.includes(r));
  return [...firstPerSource, ...rest].slice(0, MAX_REFERENCES).sort(byRank);
}
